import CategoryBanner, { CategoryBannerProps } from 'components/CategoryBanner/CategoryBanner';
import CategorySeoText from 'components/Others/CategorySeoText';
import FAQJson from 'components/Others/FAQJson';
import PostitWidget, { PostitWidgetProps } from 'components/Others/PostitWidget';
import { SeoBlock, SeoMetaTags, CategoryBannerImage } from 'constants/dataTypes';
import { getAccessorieName } from 'functions/getProductName';
import { GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import LazyHydrate from 'react-lazy-hydration';
import { getPageHeaderData, getSeoBlockContent, getSeoMetaTags, getCategoryBannerImages } from 'utils/common.utils';
import { generateImage } from 'utils/image.utils';
import { getLocalCurrency } from 'functions/currency';
import { wcApiDataService } from 'functions/wcApi';
import useWindowSize from 'hooks/useWindowSize';
import { addDataLayer } from 'lib/ga';
import ProductCardwLensColour from 'components/ProductCard/ProductCardwLensColour';
import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';
import { filterAccessoryProduct } from 'functions/filterProduct';
import React from 'react';
import CategoryBannerImageComponent from 'components/CategoryBannerImage/CategoryBannerImage';

const RecommendedProductsSlider = dynamic(
  () => import('components/Others/RecommendedProductsSlider')
);

interface AccessoryProduct {
  name: string;
  price: number;
  regular_price: number;
  category: string;
  list: string;
  position: number;
  images: string | null;
  href: string;
  priority: boolean;
  slug: string;
}

interface CategoryInfo {
  name: string;
  slug: string;
}

export default function ShopAccessories({
  accessories,
  seoBlock,
  seoMetaTags,
  categoryBannerProps,
  categoryBannerImages
}: {
  accessories: any;
  seoBlock: SeoBlock;
  seoMetaTags: SeoMetaTags;
  categoryBannerProps: CategoryBannerProps;
  categoryBannerImages: CategoryBannerImage[];
}) {

  const products: any = accessories.products;

  const categoryInfo: CategoryInfo = {
    name: accessories.name,
    slug: accessories.slug
  }

  const postitWidgetProps: PostitWidgetProps[] = accessories.meta?.postit_notes;


  const { width } = useWindowSize();
  const router = useRouter();

  const data = [
    {
      id: 1,
      href: '',
      text: 'Dresden Vision'
    },
    {
      id: 2,
      href: 'shop',
      text: 'Shop'
    },
    {
      id: 3,
      href: `shop/accessories`,
      text: `${categoryInfo.name}`
    }
  ];

  const accessoryProducts: any[] = useMemo(() => (
    [...products].map((variationNode, index) => (
      {
        name: variationNode.name,
        price: variationNode.meta.price,
        regular_price: variationNode.meta.regular_price,
        category: variationNode.name,
        slug: variationNode.slug,
        list: 'Category',
        position: index + 1,
        href: `/shop/accessories/${variationNode.slug}`,
        priority: width > 600 ? index <= 1 : index == 0
      } as AccessoryProduct
    )).flat()

  ), [products, width]);

  useEffect(() => {

    if (accessoryProducts == null) {
      return;
    }

    addDataLayer({
      event: 'ImpCompleted',
      ecommerce: {
        currencyCode: getLocalCurrency(router.locale),
        impressions: accessoryProducts.map(accessoryProduct => (
          {
            name: accessoryProduct.name,
            price: accessoryProduct.price,
            category: accessoryProduct.category,
            list: accessoryProduct.list,
            position: accessoryProduct.position
          }
        ))
      }
    });

  }, [router, accessoryProducts]);

  // Create an indexed map of banners
  const indexedBanners = useMemo(() => {
    const bannerMap = new Map<number, CategoryBannerImage>();

    categoryBannerImages.forEach(banner => {
      if (banner.bannerType === 'small-square') {
        bannerMap.set(banner.orderIndex, banner);
      } else {
        // For large rectangles, calculate the next multiple of 3
        const nextMultipleOfThree = Math.ceil(banner.orderIndex / 3) * 3 + 1;
        bannerMap.set(nextMultipleOfThree, banner);
      }
    });

    return bannerMap;
  }, [categoryBannerImages]);

  if (router.isFallback) {
    return (
      <div className="flex h-[70vh] items-center justify-center">
        Loading...
      </div>
    );
  }

  // Track the number of category banner images
  let categoryBannerImageCount = 0;

  return (
    <div>
      <Head>
        <title>Accessories | Dresden Vision</title>
        <meta name="twitter:title" content="Accessories | Dresden Vision" />
        <meta name="og:title" content="Accessories | Dresden Vision" />
        <meta
          name="description"
          content={`Accessories models with affordable prices and its wide selection are on this page. Start reviewing Accessories in various color options.`}
        />
        <meta
          property="og:description"
          content={`Accessories models with affordable prices and its wide selection are on this page. Start reviewing Accessories in various color options.`}
        />
        <meta
          name="twitter:description"
          content={`Accessories models with affordable prices and its wide selection are on this page. Start reviewing Accessories in various color options.`}
        />
      </Head>
      {/* <HeadTag {...headTagProps} /> */}
      <CategoryBanner {...categoryBannerProps} breadcrumbItems={data} />
      <div className="mx-auto max-w-[1224px] bg-white px-4 pt-4">
        <div className="mt-[24px] grid grid-cols-1 gap-y-[20px] md:grid-cols-2 md:gap-x-[15px] md:gap-y-[30px] shop:grid-cols-3">
          {accessoryProducts.map((accessoryProduct, index) => {
            const currentPosition = index + 1 + categoryBannerImageCount;
            const bannerImage = indexedBanners.get(currentPosition);
            if (bannerImage) {
              categoryBannerImageCount++;
            }

            return (
              <React.Fragment key={`accessory-${index}`}>
                {bannerImage && (
                  <div className={bannerImage.bannerType === 'large-rectangle' ? 'col-span-full' : undefined}>
                    <CategoryBannerImageComponent banner={bannerImage} width={width} />
                  </div>
                )}
                <LazyHydrate whenVisible key={`card-${index}`}>
                  <ProductCardwLensColour
                    onClickPoroduct={() => {
                      addDataLayer({
                        event: 'productClick',
                        ecommerce: {
                          click: {
                            actionField: { list: 'category' },
                            products: [
                              {
                                name: getAccessorieName(accessoryProduct.name),
                                price: accessoryProduct.price,
                                category: accessoryProduct.category,
                                list: accessoryProduct.list,
                                position: accessoryProduct.position
                              }
                            ]
                          }
                        }
                      })
                    }}
                    width={width}
                    data={{
                      images: [`${accessoryProduct.images}`],
                      title: getAccessorieName(accessoryProduct.name),
                      slug: accessoryProduct.slug,
                      price: accessoryProduct.price,
                      regularPrice: accessoryProduct.regular_price,
                      href: accessoryProduct.href,
                      prio: accessoryProduct.priority
                    }}
                  />
                </LazyHydrate>
              </React.Fragment>
            );
          })}
        </div>
        <LazyHydrate whenVisible>
          <RecommendedProductsSlider />
        </LazyHydrate>
        <LazyHydrate whenVisible>
          <CategorySeoText seoBlock={seoBlock} />
        </LazyHydrate>
        <FAQJson faq={seoBlock.faq} />
        <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
      </div>
    </div>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  let accessories: any = await wcApiDataService('cat/accessories', 'hasProducts', locale);
  accessories = filterAccessoryProduct(accessories, locale);

  if (!accessories) {
    return {
      notFound: true
    };
  }
  const seoBlock = await getSeoBlockContent('accessories', locale);
  const seoMetaTags = await getSeoMetaTags('/shop/accessories', locale);
  const categoryBannerImages = await getCategoryBannerImages('accessories', locale);

  const categoryBannerProps: CategoryBannerProps = {
    title: accessories.name,
    description: accessories.description,
    imageSrc: generateImage(accessories.slug, 'categories', 'slug', 'banner', 'jpg')
  }

  const notionPageHeaders = await getPageHeaderData('accessories', locale, categoryBannerProps);

  return {
    props: {
      accessories,
      seoBlock,
      seoMetaTags,
      categoryBannerProps: notionPageHeaders,
      categoryBannerImages
    },
  };
};
